import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/App";

function device() {
  const w = window.innerWidth;
  return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

// Public ad slot. Renders the first matching active ad for a placement.
// Records an impression on mount and a click on interaction. HTML ads render
// inside a sandboxed iframe so they can never touch the parent DOM/cookies.
export default function AdSlot({ placement, className = "", category = "", channel = "" }) {
  const { i18n } = useTranslation();
  const fr = (i18n.language || "en").startsWith("fr");
  const [ad, setAd] = useState(null);
  const [dev, setDev] = useState(device());
  const seen = useRef(false);

  useEffect(() => {
    const onResize = () => setDev(device());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/public/ads`, {
      params: { placement, device: dev, language: i18n.language, category, channel },
      withCredentials: true,
    }).then((r) => {
      if (!active) return;
      const first = (r.data.ads || [])[0] || null;
      setAd(first);
    }).catch(() => {});
    return () => { active = false; };
  }, [placement, dev, category, channel, i18n.language]);

  useEffect(() => {
    if (ad && !seen.current) {
      seen.current = true;
      axios.post(`${API}/public/ads/${ad.id}/impression`, { device: dev, language: i18n.language })
        .catch(() => {});
    }
  }, [ad, dev, i18n.language]);

  const onClick = useCallback(async (e) => {
    if (!ad) return;
    try {
      const r = await axios.post(`${API}/public/ads/${ad.id}/click`, { device: dev, language: i18n.language });
      const url = r.data?.url || ad.cta_url;
      if (url) {
        if (ad.open_new_tab) window.open(url, "_blank", "noopener");
        else window.location.href = url;
      }
    } catch { /* */ }
  }, [ad, dev, i18n.language]);

  if (!ad) return null;

  const img = (dev === "mobile" && ad.image_mobile) || (dev === "tablet" && ad.image_tablet) || ad.image_desktop || ad.image_url;
  const alt = (fr ? ad.alt_text_fr : ad.alt_text_en) || (fr ? ad.title_fr : ad.title_en) || "Advertisement";
  const title = fr ? ad.title_fr : ad.title_en;
  const cta = fr ? ad.cta_label_fr : ad.cta_label_en;

  return (
    <div className={`ad-slot ${className}`} data-testid={`ad-slot-${placement}`} data-ad-id={ad.id}>
      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]/40 mb-1">{fr ? "Publicité" : "Advertisement"}</div>
      {ad.type === "html" ? (
        <iframe
          title={`ad-${ad.id}`}
          sandbox=""
          className="w-full rounded-xl border border-white/[0.06]"
          style={{ minHeight: 120 }}
          srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:0;font-family:system-ui,sans-serif;color:#fff;background:transparent}</style></head><body>${ad.html_code || ""}</body></html>`}
          data-testid={`ad-html-${ad.id}`}
        />
      ) : (
        <button onClick={onClick} className="block w-full group" data-testid={`ad-click-${ad.id}`}>
          {img && <img src={img} alt={alt} className="w-full h-auto rounded-xl object-cover" loading="lazy" />}
          {(title || cta) && (
            <div className="flex items-center justify-between mt-2">
              {title && <span className="text-sm text-white font-medium">{title}</span>}
              {cta && <span className="text-xs px-3 py-1 rounded-full bg-[#FFC928] text-black font-semibold group-hover:bg-[#FFD85A]">{cta}</span>}
            </div>
          )}
        </button>
      )}
    </div>
  );
}
